import { useMemo, useState } from "react";
import type { ReactNode } from "react";
import type { LucideIcon } from "lucide-react";
import {
  ArrowLeft,
  Bell,
  Check,
  ChevronRight,
  Clock,
  FileText,
  Flame,
  Heart,
  Home,
  MapPin,
  MessageCircle,
  MoreHorizontal,
  Package,
  Phone,
  Play,
  Radio,
  Search,
  Share2,
  ShieldCheck,
  ShoppingCart,
  Star,
  Truck,
  User,
  Volume2,
  X,
  Zap,
} from "lucide-react";

import { ProductList3, type ProductList3Product } from "@/components/product-list3";
import { PromoBanner2 } from "@/components/promo-banner2";
import { ShoppingCart3, type ShoppingCart3Item } from "@/components/shopping-cart3";
import {
  articles,
  categories,
  findProduct,
  fromListProduct,
  money,
  productImage,
  products,
  type NineProduct,
  type Screen,
  toCartItem,
  toListProduct,
} from "@/lib/commerce-data";

const cartLabels = {
  title: "SẢN PHẨM",
  emptyTitle: "Giỏ hàng đang trống",
  emptyDescription: "Chọn thêm mã hàng để Hùng giữ hàng đẹp cho shop.",
  continueShopping: "Tiếp tục chọn hàng",
  inStock: "Đang chạy",
  remove: "Xóa",
  promoPlaceholder: "Nhập mã khác...",
  apply: "Áp dụng",
  promoHint: "Có thể thử mã VIPTANG5 để giảm 5%",
  promoEmptyError: "Nhập mã ưu đãi trước khi áp dụng",
  promoInvalidError: "Mã chưa áp dụng cho đơn này",
  orderSummary: "TÓM TẮT ĐƠN",
  subtotal: "Tạm tính",
  discount: "Giảm mã",
  shipping: "Phí giao dự kiến",
  total: "Tổng tạm tính",
  itemSingular: "mã hàng",
  itemPlural: "mã hàng",
  checkout: "Gửi thông tin đặt hàng cho Hùng",
  helper: "Mở chat Zalo với Hùng",
  shippingNote: "Gộp tuyến giao giúp tiết kiệm phí xe",
  guaranteeNote: "Sale chốt lại giá chính xác khi liên hệ",
  secureNote: "Hùng xác nhận tồn kho trước khi lên đơn",
};

const sizeRows = [
  ["42", "20kg", "465 - 480k"],
  ["48", "18kg", "435 - 450k"],
  ["56", "16kg", "415 - 430k"],
  ["72", "15kg", "395 - 410k"],
  ["90", "12kg", "375 - 390k"],
];

export function NineNgotMiniApp() {
  const [screen, setScreen] = useState<Screen>("home");
  const [previousScreen, setPreviousScreen] = useState<Screen>("home");
  const [selectedCategory, setSelectedCategory] = useState("Nho");
  const [homeFilter, setHomeFilter] = useState("Tất cả");
  const [statusFilter, setStatusFilter] = useState("Đang chạy");
  const [selectedProduct, setSelectedProduct] = useState<NineProduct>(
    findProduct("quyt-2ph"),
  );
  const [cartItems, setCartItems] = useState<ShoppingCart3Item[]>([
    toCartItem(findProduct("quyt-2ph"), 2),
    toCartItem(findProduct("tao-gala"), 1),
  ]);

  const cartCount = cartItems.reduce((total, item) => total + item.quantity, 0);

  const openDetail = (item: ProductList3Product) => {
    setSelectedProduct(fromListProduct(item));
    setPreviousScreen(screen === "detail" ? "home" : screen);
    setScreen("detail");
  };

  const addToCart = (item: ProductList3Product) => {
    const product = fromListProduct(item);
    if (product.status === "preorder") {
      setScreen("news");
      return;
    }
    setCartItems((current) => {
      const existing = current.find((cartItem) => cartItem.id === product.id);
      if (existing) {
        return current.map((cartItem) =>
          cartItem.id === product.id
            ? { ...cartItem, quantity: cartItem.quantity + 1 }
            : cartItem,
        );
      }
      return [...current, toCartItem(product)];
    });
  };

  const sharedProductProps = {
    onAddToCart: addToCart,
    onProductSelect: openDetail,
  };

  return (
    <div className="page">
      <div className="device-frame" aria-label="9 Ngọt mini-app MVP">
        <main className={screen === "detail" ? "app-content detail-mode" : "app-content"}>
          {screen === "home" && (
            <HomeScreen
              selectedCategory={selectedCategory}
              setSelectedCategory={setSelectedCategory}
              homeFilter={homeFilter}
              setHomeFilter={setHomeFilter}
              goQuick={() => setScreen("quick")}
              {...sharedProductProps}
            />
          )}
          {screen === "quick" && (
            <QuickOrderScreen
              selectedCategory={selectedCategory}
              setSelectedCategory={setSelectedCategory}
              statusFilter={statusFilter}
              setStatusFilter={setStatusFilter}
              {...sharedProductProps}
            />
          )}
          {screen === "detail" && (
            <DetailScreen
              product={selectedProduct}
              cartCount={cartCount}
              onBack={() => setScreen(previousScreen)}
              onCart={() => setScreen("cart")}
              {...sharedProductProps}
            />
          )}
          {screen === "cart" && (
            <CartScreen items={cartItems} setItems={setCartItems} />
          )}
          {screen === "news" && <NewsScreen />}
          {screen === "account" && <AccountScreen />}
        </main>

        {screen !== "detail" && (
          <BottomNav
            active={screen}
            cartCount={cartCount}
            onChange={(next) => setScreen(next)}
          />
        )}
      </div>
    </div>
  );
}

function HomeScreen({
  selectedCategory,
  setSelectedCategory,
  homeFilter,
  setHomeFilter,
  goQuick,
  onAddToCart,
  onProductSelect,
}: {
  selectedCategory: string;
  setSelectedCategory: (category: string) => void;
  homeFilter: string;
  setHomeFilter: (filter: string) => void;
  goQuick: () => void;
  onAddToCart: (product: ProductList3Product) => void;
  onProductSelect: (product: ProductList3Product) => void;
}) {
  const filteredProducts = useMemo(() => {
    const byFilter = products.filter((product) => {
      if (homeFilter === "Hàng mới") return product.status === "new";
      if (homeFilter === "Giá tốt") return Boolean(product.originalPrice);
      if (homeFilter === "Sắp về") return product.status === "preorder";
      if (homeFilter === "Nội địa") return product.origin.includes("Đà Lạt");
      return true;
    });
    return byFilter.length > 0 ? byFilter : products;
  }, [homeFilter]);

  return (
    <>
      <AppHeader />
      <SalesContactCard />
      <Ticker />

      <section className="shortcut-grid" aria-label="Danh mục trái cây">
        {categories.map((category) => (
          <button
            className={
              category.label === selectedCategory
                ? "shortcut-tile is-active"
                : "shortcut-tile"
            }
            key={category.label}
            type="button"
            onClick={() => {
              if (category.label !== "Tất cả") setSelectedCategory(category.label);
              goQuick();
            }}
          >
            <span className="shortcut-fruit">{category.fruit}</span>
            <span>{category.label}</span>
          </button>
        ))}
      </section>

      <PromoVideoCard />

      <ProductList3
        title="Sản phẩm phù hợp"
        products={products.slice(0, 5).map(toListProduct)}
        variant="rail"
        onAddToCart={onAddToCart}
        onProductSelect={onProductSelect}
      />

      <ProductList3
        title="Hàng mới về"
        products={products.filter((product) => product.status === "new").map(toListProduct)}
        variant="rail"
        onAddToCart={onAddToCart}
        onProductSelect={onProductSelect}
      />

      <PromoBanner2
        className="flash-banner registry-promo-banner"
        message="FLASH SALE HÔM NAY"
        discount="Giảm đến 15% hàng nhập"
        linkText="Xem ngay"
      />

      <section className="section all-products">
        <div className="section-heading">
          <h2>Tất cả sản phẩm</h2>
        </div>
        <div className="chip-row" aria-label="Bộ lọc sản phẩm">
          {["Tất cả", "Hàng mới", "Giá tốt", "Sắp về", "Nội địa"].map((filter) => (
            <button
              className={homeFilter === filter ? "chip is-active" : "chip"}
              key={filter}
              type="button"
              onClick={() => setHomeFilter(filter)}
            >
              {filter}
            </button>
          ))}
        </div>
        <ProductList3
          products={filteredProducts.slice(0, 5).map(toListProduct)}
          variant="rows"
          onAddToCart={onAddToCart}
          onProductSelect={onProductSelect}
        />
      </section>
    </>
  );
}

function QuickOrderScreen({
  selectedCategory,
  setSelectedCategory,
  statusFilter,
  setStatusFilter,
  onAddToCart,
  onProductSelect,
}: {
  selectedCategory: string;
  setSelectedCategory: (category: string) => void;
  statusFilter: string;
  setStatusFilter: (status: string) => void;
  onAddToCart: (product: ProductList3Product) => void;
  onProductSelect: (product: ProductList3Product) => void;
}) {
  const categoryMeta =
    categories.find((category) => category.label === selectedCategory) ?? categories[0];
  const subcategories =
    selectedCategory === "Táo"
      ? ["Tất cả", "Táo đỏ", "Táo xanh", "Táo vàng"]
      : ["Tất cả", "Nho xanh", "Nho đỏ", "Nho đen"];
  const statusChips: { label: string; icon: LucideIcon }[] = [
    { label: "Đang chạy", icon: Flame },
    { label: "Hàng mới", icon: Package },
    { label: "Sắp về", icon: Truck },
    { label: "Sắp hết", icon: Bell },
  ];
  const categoryProducts = products.filter(
    (product) => product.category === selectedCategory,
  );
  const visibleProducts = categoryProducts.filter((product) => {
    if (statusFilter === "Hàng mới") return product.status === "new";
    if (statusFilter === "Sắp về") return product.status === "preorder";
    if (statusFilter === "Sắp hết") return product.status === "low";
    return product.status !== "preorder";
  });
  const productSet = visibleProducts.length > 0 ? visibleProducts : categoryProducts;

  return (
    <>
      <header className="screen-header">
        <button className="icon-button" type="button" aria-label="Quay lại">
          <ArrowLeft size={20} />
        </button>
        <div>
          <h1>{selectedCategory}</h1>
          <p>{categoryMeta.skuCount} SKU</p>
        </div>
        <button className="icon-button" type="button" aria-label="Tìm kiếm">
          <Search size={18} />
        </button>
      </header>

      <div className="chip-row subcategory-row" aria-label="Danh mục con">
        {subcategories.map((chip, index) => (
          <button
            className={index === 0 ? "chip is-active" : "chip"}
            key={chip}
            type="button"
            onClick={() => {
              if (chip === "Tất cả") return;
              setSelectedCategory(chip.split(" ")[0]);
            }}
          >
            {chip}
          </button>
        ))}
      </div>

      <div className="chip-row status-row" aria-label="Trạng thái hàng">
        {statusChips.map(({ label, icon: Icon }) => (
          <button
            className={statusFilter === label ? "chip status-chip is-active" : "chip status-chip"}
            key={label}
            type="button"
            onClick={() => setStatusFilter(label)}
          >
            <Icon size={15} />
            {label}
          </button>
        ))}
      </div>

      <p className="count-line">
        Hiển thị {productSet.length} SKU · {statusFilter}
      </p>

      <ProductList3
        className="category-list"
        products={productSet.map(toListProduct)}
        variant="rows"
        onAddToCart={onAddToCart}
        onProductSelect={onProductSelect}
      />

      <section className="section mini-account-card">
        <div>
          <p className="muted-label">Sale phụ trách</p>
          <h2>Hùng đang giữ size đẹp cho shop</h2>
        </div>
        <button className="secondary-button blue" type="button">
          <MessageCircle size={16} />
          Nhắn tư vấn
        </button>
      </section>
    </>
  );
}

function DetailScreen({
  product,
  cartCount,
  onBack,
  onCart,
  onAddToCart,
  onProductSelect,
}: {
  product: NineProduct;
  cartCount: number;
  onBack: () => void;
  onCart: () => void;
  onAddToCart: (product: ProductList3Product) => void;
  onProductSelect: (product: ProductList3Product) => void;
}) {
  const related = products
    .filter((item) => item.id !== product.id && item.status !== "preorder")
    .slice(0, 4)
    .map(toListProduct);
  const listProduct = toListProduct(product);

  return (
    <>
      <header className="detail-header">
        <button className="icon-button ghost" type="button" onClick={onBack} aria-label="Quay lại">
          <ArrowLeft size={20} />
        </button>
        <h1>Chi tiết sản phẩm</h1>
        <button className="warranty-button" type="button">
          <ShieldCheck size={16} />
          Bảo hàng
        </button>
      </header>

      <section className="detail-media registry-detail-media">
        <img src={productImage(product)} alt={product.name} />
        <div className="media-pill">Hàng mới hôm nay lúc 06:30</div>
        <button className="float-button favorite" type="button" aria-label="Yêu thích">
          <Heart size={18} />
        </button>
        <button className="play-button large" type="button" aria-label="Phát video">
          <Play size={34} fill="currentColor" />
        </button>
        <button className="float-button share" type="button" aria-label="Chia sẻ">
          <Share2 size={18} />
        </button>
      </section>

      <div className="thumb-strip" aria-label="Media sản phẩm">
        {[1, 2, 3, 4, 5].map((thumb) => (
          <button className={thumb === 1 ? "thumb is-active" : "thumb"} key={thumb} type="button">
            <img src={productImage(product)} alt="" />
          </button>
        ))}
        <span className="media-count">1 / 7</span>
      </div>

      <section className="detail-summary">
        <p className="detail-price">
          {money(product.price)}
          <span>/thùng</span>
          <em>{product.perKg}</em>
        </p>
        <h2>{product.name}</h2>
        <div className="summary-meta">
          <span>Đã bán: {product.soldCount} thùng</span>
          <span>Xuất xứ: {product.origin}</span>
        </div>
        <p className="checked-line">
          <Check size={14} />
          Đức Anh kiểm hàng · 06:30 AM
        </p>
      </section>

      <section className="section size-table-section">
        <table className="size-table">
          <thead>
            <tr>
              <th>SIZE</th>
              <th>QUY CÁCH</th>
              <th>GIÁ / THÙNG</th>
            </tr>
          </thead>
          <tbody>
            {sizeRows.map((row) => (
              <tr key={row[0]}>
                <td>{row[0]}</td>
                <td>{row[1]}</td>
                <td>{row[2]}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      <section className="section lot-review">
        <div className="section-heading">
          <h2>Đánh giá lô hàng hôm nay</h2>
        </div>
        <div className="note-box">
          <p className="note-title">ĐẶC ĐIỂM LÔ HÀNG</p>
          <p>
            Lô về <strong>5h sáng 21/05</strong>, đã lọc tại kho Hà Đông.
            Khoảng <strong>5-7% vết nhỏ ở cuống</strong> đã loại bỏ trước khi
            đóng thùng. Bảo quản mát 5-7 ngày, xoay vòng nhanh.
          </p>
        </div>
      </section>

      <ProductList3
        title="Mã hàng phù hợp"
        subtitle="Gộp 1 chuyến xe - tiết kiệm ship 30-40%"
        products={related}
        variant="rail"
        onAddToCart={onAddToCart}
        onProductSelect={onProductSelect}
      />

      <div className="detail-action-bar">
        <button className="secondary-button" type="button">
          <MessageCircle size={16} />
          Hỏi sale
        </button>
        <button className="secondary-button cart-view" type="button" onClick={onCart}>
          <ShoppingCart size={16} />
          Xem giỏ
          {cartCount > 0 && <span>{cartCount}</span>}
        </button>
        <button
          className="primary-button detail-add"
          type="button"
          onClick={() => onAddToCart(listProduct)}
        >
          + Thêm vào giỏ
        </button>
      </div>
    </>
  );
}

function CartScreen({
  items,
  setItems,
}: {
  items: ShoppingCart3Item[];
  setItems: (items: ShoppingCart3Item[]) => void;
}) {
  return (
    <>
      <header className="screen-header centered">
        <button className="icon-button" type="button" aria-label="Quay lại">
          <ArrowLeft size={20} />
        </button>
        <h1>Giỏ hàng ({items.length})</h1>
        <MoreHorizontal size={20} />
      </header>

      <NumberedSection number={1} title="THÔNG TIN GIAO HÀNG">
        <InfoLine icon={User} label="NGƯỜI NHẬN" value="Nguyễn Anh Tấn · 0901 234 567" />
        <InfoLine
          icon={MapPin}
          label="ĐỊA CHỈ GIAO HÀNG"
          value="123 Lê Lợi, P. Bến Thành, Q.1, TP. Hồ Chí Minh"
        />
      </NumberedSection>

      <NumberedSection number={2} title={`SẢN PHẨM (${items.length})`}>
        <ShoppingCart3
          items={items}
          onItemsChange={setItems}
          labels={cartLabels}
          locale="vi-VN"
          currency="VND"
          validPromoCode="VIPTANG5"
          discountRate={0.05}
          shipping={0}
        />
      </NumberedSection>

      <NumberedSection number={3} title="GHI CHÚ ĐƠN HÀNG">
        <textarea aria-label="Ghi chú đơn hàng" placeholder="Vd: chọn trái to xanh, giao trước 9h sáng..." />
      </NumberedSection>
    </>
  );
}

function NewsScreen() {
  return (
    <>
      <header className="compact-header">
        <span className="brand-mark">9</span>
        <h1>Có gì mới</h1>
        <MoreHorizontal size={20} />
      </header>

      <section className="live-card">
        <div className="live-topline">
          <span className="live-badge">LIVE</span>
          <span className="viewer-pill">47 đang xem</span>
        </div>
        <div className="live-fruit">🍇</div>
        <button className="play-button large" type="button" aria-label="Xem live">
          <Play size={30} fill="currentColor" />
        </button>
        <div className="live-caption">
          <span className="avatar-small">H</span>
          <div>
            <strong>Hùng</strong>
            <p>Hùng đang giới thiệu lô nho Autumn Royal mới về</p>
          </div>
        </div>
        <span className="product-tag">🍇 Nho Autumn Royal</span>
      </section>

      <section className="section article-section">
        <div className="section-heading">
          <h2>Bài viết mới nhất</h2>
          <span>12 bài</span>
        </div>
        <div className="article-list">
          {articles.map(([topic, title, fruit], index) => (
            <article className="article-row" key={title}>
              <div className={`article-thumb tone-${index % 5}`}>{fruit}</div>
              <div>
                <span>{topic}</span>
                <h3>{title}</h3>
                <p>
                  <Clock size={13} />
                  {index + 3} phút đọc
                </p>
              </div>
              <Bell className="article-pin" size={17} />
            </article>
          ))}
        </div>
      </section>
    </>
  );
}

function AccountScreen() {
  return (
    <>
      <header className="plain-title">
        <h1>Tài khoản</h1>
      </header>

      <section className="profile-card">
        <div className="profile-main">
          <span className="profile-avatar">👨‍💼</span>
          <div>
            <h2>Anh Tấn</h2>
            <p>Cửa hàng trái cây Đức Anh</p>
            <small>U11-L05 Do Nghĩa Urban Area, Hà Đông, Hà Nội</small>
            <button type="button">Cập nhật địa chỉ</button>
          </div>
          <ChevronRight size={18} />
        </div>
        <div className="profile-sale-row">
          <span className="avatar-small">H</span>
          <div>
            <small>Sale phụ trách</small>
            <strong>Hùng</strong>
          </div>
          <button type="button">
            <MessageCircle size={14} />
            Nhắn Zalo
          </button>
          <button type="button">
            <Phone size={14} />
            Gọi
          </button>
        </div>
      </section>

      <section className="loyalty-card">
        <div className="section-heading">
          <h2>
            <Star size={18} fill="currentColor" />
            Điểm tích lũy
          </h2>
        </div>
        <strong>20</strong>
        <span>điểm</span>
        <p>Còn <b>10 điểm</b> để nhận ưu tiên giữ hàng đẹp</p>
        <small>HSD: 02/12/2026</small>
      </section>

      <section className="zalo-card">
        <span className="zalo-icon">💙</span>
        <div>
          <h2>Quan tâm Zalo OA</h2>
          <p>Nhận tin hàng mới, hàng sắp về, biến động giá và thông tin quan trọng.</p>
        </div>
        <span className="followed">
          <Check size={13} />
          Đã quan tâm
        </span>
      </section>
    </>
  );
}

function AppHeader() {
  return (
    <header className="home-header">
      <div>
        <p>👋 Chào buổi sáng,</p>
        <h1>
          Anh Tấn <span>Nhóm A</span>
        </h1>
      </div>
      <div className="header-actions">
        <button className="icon-button" type="button" aria-label="Tìm kiếm">
          <Search size={18} />
        </button>
        <button className="icon-button" type="button" aria-label="Menu mini-app">
          <MoreHorizontal size={18} />
        </button>
        <button className="icon-button" type="button" aria-label="Đóng">
          <X size={18} />
        </button>
      </div>
    </header>
  );
}

function SalesContactCard() {
  return (
    <section className="sales-card" aria-label="Sale phụ trách">
      <div className="sales-person">
        <span className="sales-avatar">👨‍💼</span>
        <div>
          <p>Sale phụ trách</p>
          <h2>Hùng</h2>
          <span>Online</span>
        </div>
      </div>
      <div className="sales-actions">
        <button className="secondary-button" type="button">
          <FileText size={15} />
          Báo giá
        </button>
        <button className="primary-button" type="button">
          <MessageCircle size={15} />
          Chat tư vấn
        </button>
      </div>
    </section>
  );
}

function Ticker() {
  return (
    <div className="ticker" aria-label="Tin hàng mới">
      <span>06:20 sáng</span>
      <p>Nho BAY Úc vừa về · Cherry Mỹ còn 15 thùng · Chanh vàng giảm 7%</p>
    </div>
  );
}

function PromoVideoCard() {
  return (
    <section className="promo-video" aria-label="Video hàng tại kho">
      <div className="video-status">
        <Radio size={14} />
        Quay tại kho · 8:30 sáng nay
      </div>
      <button className="icon-button volume" type="button" aria-label="Tắt tiếng">
        <Volume2 size={15} />
      </button>
      <div className="video-fruit">🍇</div>
      <button className="play-button" type="button" aria-label="Phát video">
        <Play size={24} fill="currentColor" />
      </button>
      <span className="duration">0:27</span>
    </section>
  );
}

function NumberedSection({
  number,
  title,
  children,
}: {
  number: number;
  title: string;
  children: ReactNode;
}) {
  return (
    <section className="numbered-section">
      <h2>
        <span>{number}</span>
        {title}
      </h2>
      {children}
    </section>
  );
}

function InfoLine({
  icon: Icon,
  label,
  value,
}: {
  icon: LucideIcon;
  label: string;
  value: string;
}) {
  return (
    <div className="info-line">
      <span className="info-icon">
        <Icon size={16} />
      </span>
      <div>
        <small>{label}</small>
        <p>{value}</p>
      </div>
      <button type="button">Đổi</button>
    </div>
  );
}

function BottomNav({
  active,
  cartCount,
  onChange,
}: {
  active: Screen;
  cartCount: number;
  onChange: (screen: Screen) => void;
}) {
  const navItems: { id: Screen; label: string; icon: LucideIcon }[] = [
    { id: "home", label: "Trang chủ", icon: Home },
    { id: "news", label: "Có gì mới", icon: Zap },
    { id: "quick", label: "Đặt hàng nhanh", icon: MessageCircle },
    { id: "cart", label: "Giỏ hàng", icon: ShoppingCart },
    { id: "account", label: "Tài khoản", icon: User },
  ];

  return (
    <nav className="bottom-nav" aria-label="Điều hướng chính">
      {navItems.map(({ id, label, icon: Icon }) => (
        <button
          className={`${active === id ? "is-active" : ""} ${id === "quick" ? "quick-nav" : ""}`}
          key={id}
          type="button"
          onClick={() => onChange(id)}
        >
          <span className="nav-icon-wrap">
            <Icon size={id === "quick" ? 24 : 22} />
            {id === "cart" && cartCount > 0 && <em>{cartCount}</em>}
          </span>
          <span>{active === "news" && id === "quick" ? "Nhắn sale" : label}</span>
        </button>
      ))}
    </nav>
  );
}
